import { Card, Loading } from "@common";
import { formatMoney } from "@utils/format";
import { pickSelectedPractitionerRow, mapTotalsFromRow } from "@utils/monthTotalsUtils";

const METRICS = [
  { label: "Total Production", key: "total_production", format: "money" },
  { label: "Total Collections", key: "total_collection", format: "money" },
  {
    label: "Total Lab Costs",
    key: "total_lab_costs",
    format: "money",
    backendKey: "lab_costs",
    ddsOnly: true,
  },
  {
    label: "Total Material Costs",
    key: "total_material_costs",
    format: "money",
    backendKey: "material_costs",
    ddsOnly: true,
  },
  {
    label: "Total Invisalign Costs",
    key: "total_invisalign_costs",
    format: "money",
    backendKey: "invisalign_costs",
    ddsOnly: true,
  },
  { label: "Total Hours Worked", key: "total_hours_worked", format: "hours" },
  { label: "Total Days Worked", key: "total_per_day_number", format: "hours" },
];

function formatMetricValue(format, value) {
  if (format === "hours") return Number(value || 0).toLocaleString("en-US");
  return formatMoney(value);
}

export default function TotalMonthData({
  clinicId,
  month,
  practitionerName,
  practitionerTypeUi,
  loading,
  error,
  data,
  fallbackReport,
  hiddenColumns = [],
  pdfSuggestedLabCost = 0,
  onPdfSuggestedLabCostClick,

  // mismatch compare inputs
  expectedTotals = null,
  shouldCheckMismatch = false,
}) {
  const selectedRow = pickSelectedPractitionerRow(data, practitionerName);

  const totals = mapTotalsFromRow(selectedRow);

  const isHyg = practitionerTypeUi?.toUpperCase() === "HYG";

  const visibleMetrics = METRICS.filter((m) => {
    if (m.ddsOnly && isHyg) return false;
    if (m.backendKey && hiddenColumns.includes(m.backendKey)) return false;
    return true;
  });

  // Per-tier other_deduction hour totals (sum of `hours` across the
  // month's daily reports, grouped by tier label).
  const otherDeductionHourMetrics = (totals.other_deduction_hours || [])
    .filter((entry) => Number(entry?.hours) > 0)
    .map((entry) => ({
      label: `Total Hours - ${entry.label}`,
      key: `od_hours::${entry.label}`,
      format: "hours",
      value: Number(entry.hours) || 0,
    }));

  const allMetrics = [
    ...visibleMetrics.map((m) => ({ ...m, value: totals[m.key] })),
    ...(!isHyg && !hiddenColumns.includes("lab_costs") && Number(pdfSuggestedLabCost || 0) > 0
      ? [
          {
            label: "PDF Suggested Lab Costs",
            key: "pdf_suggested_lab_costs",
            format: "money",
            value: Number(pdfSuggestedLabCost || 0),
            clickable: typeof onPdfSuggestedLabCostClick === "function",
          },
        ]
      : []),
    ...otherDeductionHourMetrics,
  ];

  const hasAnyTotals = allMetrics.some((m) => Number(m.value || 0) !== 0);

  return (
    <Card>
      <div className="mb-4">
        <div className="text-sm font-semibold text-palette-slate-badge">Total Data</div>

        <div className="flex items-center justify-between gap-3">
          <div className="text-lg font-bold text-palette-slate-primary leading-tight">
            {clinicId || "—"} — {month || "—"}
          </div>
        </div>

        <div className="mt-1 text-xs text-palette-gray-secondary">
          Selected practitioner totals for the month
        </div>

        <div className="mt-2 text-xs text-palette-gray-badge">
          Practitioner:{" "}
          <span className="font-semibold">
            {practitionerName || "—"}
            {practitionerTypeUi ? " (" + practitionerTypeUi + ")" : ""}
          </span>
        </div>

        {error && <div className="mt-2 text-xs text-palette-red-badge">{error}</div>}

        {!error && !selectedRow && (
          <div className="mt-2 text-xs text-palette-amber-heading">
            No totals returned for this practitioner/month (likely not submitted yet).
          </div>
        )}

        {!loading && !error && selectedRow && !hasAnyTotals && (
          <div className="mt-2 text-xs text-palette-amber-heading">
            Totals exist but all values are 0.00 for this practitioner/month.
          </div>
        )}
      </div>

      {loading ? (
        <Loading text="Loading practitioner totals..." size="sm" />
      ) : (
        <div className="rounded-xl border border-palette-gray-divider overflow-hidden">
          <div className="grid grid-cols-2 text-sm">
            <div className="px-4 py-3 border-b border-palette-gray-divider font-semibold bg-palette-gray-soft">
              Metric
            </div>
            <div className="px-4 py-3 border-b border-palette-gray-divider font-semibold bg-palette-gray-soft text-right">
              Total
            </div>

            {allMetrics.map((m, idx) => {
              const isLast = idx === allMetrics.length - 1;
              const borderCls = isLast ? "" : "border-b border-palette-gray-subtle";

              return (
                <div key={m.key} className="contents">
                  <div className={`px-4 py-3 ${borderCls}`}>{m.label}</div>
                  <div className={`px-4 py-3 ${borderCls} text-right font-semibold`}>
                    {m.clickable ? (
                      <button
                        type="button"
                        className="font-semibold text-palette-indigo-heading underline decoration-palette-indigo-border underline-offset-2 hover:text-palette-indigo-primary"
                        onClick={onPdfSuggestedLabCostClick}
                      >
                        {formatMetricValue(m.format, m.value)}
                      </button>
                    ) : (
                      formatMetricValue(m.format, m.value)
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ✅ FINAL: only show warning if record exists (Cost & Production was created/filled) */}
      {!loading && !error && fallbackReport && fallbackReport.id && (
        <div className="mt-3 rounded-xl border border-palette-amber-border bg-palette-amber-soft px-3 py-2 text-palette-amber-primary text-xs">
          <div className="font-semibold flex items-center gap-2">
            <span aria-hidden="true">⚠️</span>
            <span>Warning</span>
          </div>
          <div className="mt-1">
            Once submitted, monthly reconciliation entries cannot be changed by practitioners.
          </div>
        </div>
      )}
    </Card>
  );
}
